import { ResponseMetadata } from './common'

/**
 * UI-facing notification channel enum
 * Used by UI components, transformed to/from API boolean flags
 */
export enum NotificationChannel {
  EMAIL = 'email',
  IN_APP = 'in_app',
  BOTH = 'both',
  NONE = 'none',
}

/**
 * Channel settings for a specific notification event (API format)
 */
export interface NotificationChannelSettings {
  /** Whether email notifications are enabled for this event */
  email: boolean
  /** Whether in-app notifications are enabled for this event */
  in_app: boolean
}

/**
 * Notification preference configuration object
 * Maps event types to their channel settings
 */
export interface PreferenceConfig {
  /** Notification settings for new comments */
  comment_new: NotificationChannelSettings
  /** Notification settings for resolved comments */
  comment_resolved: NotificationChannelSettings
  /** Notification settings for comment mentions */
  comment_mention: NotificationChannelSettings
  /** Notification settings for deadlines */
  workflow_deadline: NotificationChannelSettings
  /** Notification settings for assignments */
  deliverable_assign: NotificationChannelSettings
}

/**
 * User notification preferences
 */
export interface NotificationPreferences {
  user_id: string
  /** All notification preferences in a single config object */
  preference_config: PreferenceConfig
}

/**
 * Standard success response for getting notification preferences
 * API returns an array with a single item
 */
export interface SuccessResponseGetNotificationPreferences {
  /** Always true for success responses */
  success: boolean
  /** Array containing user notification preferences (single item) */
  data: NotificationPreferences[]
  /** Response metadata */
  metadata: ResponseMetadata
  /** Pagination info (null for this endpoint) */
  pagination: null
  /** Related resource links (null for this endpoint) */
  links: null
  /** Non-blocking warnings */
  warnings: string[] | null
}

/**
 * Request body for updating notification preferences
 */
export interface RequestUpdateNotificationPreferences {
  /** All notification preferences in a single config object */
  preference_config?: PreferenceConfig
}

/**
 * Request body for creating notification preferences (if needed by BE)
 */
export interface RequestCreateNotificationPreferences {
  /** User ID to create preferences for */
  user_id: string
  /** All notification preferences in a single config object */
  preference_config: PreferenceConfig
}

/**
 * Standard success response for updating notification preferences
 */
export interface SuccessResponseUpdateNotificationPreferences {
  /** Always true for success responses */
  success: boolean
  /** Array containing updated notification preferences (single item) */
  data: NotificationPreferences[]
  /** Response metadata */
  metadata: ResponseMetadata
  /** Pagination info (null for this endpoint) */
  pagination: null
  /** Related resource links (null for this endpoint) */
  links: null
  /** Non-blocking warnings */
  warnings: string[] | null
}
